if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    //商品页面HTML模块
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      gioVariantRequestError = '';
      gioVariantRequestStartedAt = 0;
      variantRequestSequence = 0;
      variantRequest = null;
      lastVariantRequest = null;
      variantStartupGeneration = 0;
      variantStartupPromise = null;
      variantStartupSelectionQueued = false;
      optionalComponentUpdates = new Map();
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }
      //进入商品页面的时候会执行这个方法
      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onEditorRoutePageShow = (event) => {
          // A back-forward cache can restore the previous page's navigation
          // lock. Rebuild its original selected-variant controls in that case.
          if (event.persisted && this.dataset.gioEditorRouteChanging === 'true') window.location.reload();
          this.expireVariantRequest();
        };
        window.addEventListener('pageshow', this.onEditorRoutePageShow);
        this.onVariantRequestVisibility = () => {
          if (document.visibilityState !== 'hidden') this.expireVariantRequest();
        };
        document.addEventListener('visibilitychange', this.onVariantRequestVisibility);

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        this.scheduleVariantStartupReconcile();
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        window.removeEventListener('pageshow', this.onEditorRoutePageShow);
        document.removeEventListener('visibilitychange', this.onVariantRequestVisibility);
        this.variantRequestSequence += 1;
        this.variantStartupGeneration += 1;
        this.variantStartupPromise = null;
        this.optionalComponentUpdates.clear();
        if (this.variantRequest) clearTimeout(this.variantRequest.timer);
        this.variantRequest = null;
        this.abortController?.abort();
      }

      scheduleVariantStartupReconcile() {
        if (this.variantStartupPromise) return this.variantStartupPromise;
        const generation = this.variantStartupGeneration;
        const dependencies = [];
        if (typeof customElements.whenDefined === 'function') {
          if (this.variantSelectors) dependencies.push(customElements.whenDefined('variant-selects'));
          if (this.productForm) dependencies.push(customElements.whenDefined('product-form'));
        }
        const pending = Promise.all(dependencies).then(() => {
          if (this.variantStartupPromise === pending) this.variantStartupPromise = null;
          if (this.isConnected === false || generation !== this.variantStartupGeneration) return;
          this.reconcileStartupVariantSelection();
        }).catch((error) => {
          if (this.variantStartupPromise === pending) this.variantStartupPromise = null;
          console.error('Product options could not be initialized', error);
        });
        this.variantStartupPromise = pending;
        return pending;
      }

      reconcileStartupVariantSelection() {
        // Controls can be clicked while deferred scripts are still arriving.
        // Their live values survive; the earlier change event does not. Read the
        // latest choices once the real request/form controllers are available.
        const picker = this.variantSelectors;
        const form = this.productForm;
        if (!picker || form && typeof form.toggleSubmitButton !== 'function' ||
            this.variantRequest || this.pendingRequestUrl || this.gioVariantRequestError ||
            this.dataset.gioEditorRouteChanging === 'true') return;
        const groups = Array.from(picker.querySelectorAll('fieldset, .product-form__input--dropdown'));
        if (!groups.length) return;
        const controls = groups.map((group) => group.querySelector('input[type="radio"]:checked, select'));
        const selected = controls.map((input) => input?.tagName === 'SELECT' ? input.selectedOptions[0] : input);
        if (selected.some((input) => !input?.dataset.optionValueId)) return;
        const choices = controls.map((input) => String(input.value));
        const variant = this.getSelectedVariant(this);
        const original = variant?.options;
        const nativeId = form?.querySelector('input[name="id"]')?.value;
        const changedIndex = Array.isArray(original)
          ? choices.findIndex((value, index) => value !== String(original[index]))
          : selected.findIndex((input) => input.tagName === 'OPTION' ? !input.defaultSelected : !input.defaultChecked);
        const sameOptions = Array.isArray(original) && choices.length === original.length && changedIndex === -1;
        const sameId = !form || String(nativeId || '') === String(variant?.id || '');
        if (!this.variantStartupSelectionQueued && (sameOptions && sameId || !variant && changedIndex === -1)) return;
        this.variantStartupSelectionQueued = false;
        const index = changedIndex < 0 ? 0 : changedIndex;
        this.handleOptionValueChange({ data: {
          event: { target: controls[index] }, target: selected[index],
          selectedOptionValues: selected.map((input) => input.dataset.optionValueId),
          startupSelectionReplay: true,
        } });
      }

      deferUnreadyVariantSelection() {
        this.variantStartupSelectionQueued = true;
        // Do not allow the old hidden variant to be purchased while the form's
        // custom element has not upgraded. Only a confirmed response releases it.
        const submit = this.productForm?.querySelector('[type="submit"]');
        if (submit) { submit.disabled = true; submit.setAttribute('aria-disabled', 'true'); }
        this.querySelectorAll('#customButton, #inputImage.product-info_atc_btn').forEach((button) => {
          button.setAttribute('aria-disabled', 'true');
        });
        this.scheduleVariantStartupReconcile();
      }

      updateOptionalComponent(selector, method, args) {
        const node = this.querySelector(selector);
        if (!node) return;
        const key = selector + ':' + method;
        const update = { node, args, sequence: this.variantRequestSequence };
        this.optionalComponentUpdates.set(key, update);
        const apply = () => {
          if (this.isConnected === false || !node.isConnected || this.querySelector(selector) !== node ||
              this.optionalComponentUpdates.get(key) !== update || update.sequence !== this.variantRequestSequence) return;
          if (typeof node[method] !== 'function') return;
          this.optionalComponentUpdates.delete(key);
          try { node[method](...args); }
          catch (error) { console.warn('Optional product details could not be refreshed', error); }
        };
        if (typeof node[method] === 'function') apply();
        else if (typeof customElements.whenDefined === 'function') customElements.whenDefined(node.localName).then(apply);
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }
      //点击sku选项触发的事件
      handleOptionValueChange({ data: { event, target, selectedOptionValues, startupSelectionReplay = false } }) {
        if(event.target.outerHTML.includes('-layer')){   //点击弹窗sku的逻辑
          const variantPickerContainer = document.getElementById('variant-picker-container');
          const htmlContent = variantPickerContainer.innerHTML; 
        }else if (!this.contains(event.target)){
          return;
        } 
        if (this.productForm && typeof this.productForm.toggleSubmitButton !== 'function') {
          this.deferUnreadyVariantSelection();
          return;
        }
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, { startupSelectionReplay }),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        // A selected option is only confirmed after Shopify has returned its
        // section. Keep that safety lock on failures, but expose a retryable
        // error instead of leaving the editor in an endless waiting state.
        const sequence = ++this.variantRequestSequence;
        if (this.variantRequest) clearTimeout(this.variantRequest.timer);
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        const pendingUrl = this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = pendingUrl;
        this.gioVariantRequestError = '';
        this.gioVariantRequestStartedAt = Date.now();
        const request = {
          sequence, controller, pendingUrl,
          startedAt: this.gioVariantRequestStartedAt,
          timer: null,
        };
        this.variantRequest = request;
        this.lastVariantRequest = { requestUrl, targetId, callback, pendingUrl };
        request.timer = setTimeout(() => this.failVariantRequest(request), 15000);
        this.notifyVariantRequest('loading');

        // Promise.resolve also catches synchronous fetch failures (for example
        // when a browser extension or a suspended page rejects the request).
        return Promise.resolve()
          .then(() => {
            if (!this.isCurrentVariantRequest(request)) return null;
            return fetch(requestUrl, { signal: controller.signal });
          })
          .then((response) => {
            if (!this.isCurrentVariantRequest(request)) return null;
            if (!response?.ok) throw new Error('Product options request failed');
            return response.text();
          })
          .then((responseText) => {
            // Abort alone does not guard a response whose body has already
            // started resolving. A late response must never replace a newer
            // selection, even after a background tab resumes.
            if (!this.isCurrentVariantRequest(request)) return;
            if (this.expireVariantRequest()) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const productInfo = html.querySelector('product-info');
            if (!productInfo) throw new Error('Product options response is incomplete');
            const variantPicker = productInfo.querySelector('variant-selects');
            if (variantPicker) {
              const selectedVariant = variantPicker.querySelector('[data-selected-variant]');
              if (!selectedVariant) throw new Error('Product options response is incomplete');
              // A JSON null is Shopify's legitimate unavailable combination.
              const variant = JSON.parse(selectedVariant.textContent);
              if (variant !== null && (!variant || typeof variant !== 'object' || !variant.id)) {
                throw new Error('Product options response is invalid');
              }
            }
            this.pendingRequestUrl = null;
            callback(html);
            if (!this.isCurrentVariantRequest(request)) return;
            clearTimeout(request.timer);
            this.variantRequest = null;
            this.gioVariantRequestStartedAt = 0;
            this.gioVariantRequestError = '';
            this.notifyVariantRequest('ready');
            // set focus to last clicked option value
            document.getElementById(targetId)?.focus();
          })
          .catch((error) => {
            if (!this.isCurrentVariantRequest(request)) return;
            this.failVariantRequest(request);
            console.error('Product options could not be updated', error);
          });
      }

      isCurrentVariantRequest(request) {
        return this.variantRequest === request && this.variantRequestSequence === request.sequence;
      }

      expireVariantRequest() {
        const request = this.variantRequest;
        if (!request || Date.now() - request.startedAt < 15000) return false;
        this.failVariantRequest(request);
        return true;
      }

      failVariantRequest(request) {
        if (!this.isCurrentVariantRequest(request)) return;
        clearTimeout(request.timer);
        this.variantRequest = null;
        request.controller.abort();
        this.gioVariantRequestStartedAt = 0;
        this.pendingRequestUrl = request.pendingUrl;
        this.gioVariantRequestError = 'We couldn’t update your options. Retry or choose another option.';
        this.productForm?.toggleSubmitButton(true);
        this.productForm?.handleErrorMessage(this.gioVariantRequestError);
        this.notifyVariantRequest('error');
      }

      retryVariantRequest() {
        if (!this.lastVariantRequest || this.variantRequest || this.dataset.gioEditorRouteChanging === 'true') return false;
        this.pendingRequestUrl = this.lastVariantRequest.pendingUrl;
        this.resetProductFormState();
        return this.renderProductInfo(this.lastVariantRequest);
      }

      notifyVariantRequest(state) {
        this.dispatchEvent(new CustomEvent('gio:variant-request-state', { bubbles: true, detail: { state } }));
      }
      //切换sku
      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }
      //更改商品sku
      handleUpdateProductInfo(productUrl, { startupSelectionReplay = false } = {}) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          if (this.redirectForEditorRoute(html, variant, productUrl)) return;

          if (this.pickupAvailability) {
            // Pickup's script is deferred after product-info. Keep its startup
            // fetch on the latest confirmed ID, without making it a purchase gate.
            this.pickupAvailability.dataset.variantId = variant?.id || '';
            this.updateOptionalComponent('pickup-availability', 'update', [variant]);
          }
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          // Every confirmed option path (mouse, keyboard, dropdown or modal)
          // must commit its crop geometry before the product becomes ready.
          const managedGeometry = window.GIOProductGeometry && window.GIOProductGeometry.sync(this, variant);
          // The missed startup click may also have skipped a legacy label's
          // photo/text initialization. Only that replay needs the scoped hook;
          // normal clicks and managed crop geometry already own their updates.
          if (startupSelectionReplay && !managedGeometry && typeof this.gioLayerVariantSync !== 'function' &&
              typeof this.gioReplayCustomizerVariant === 'function') {
            this.gioReplayCustomizerVariant(variant);
          }

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
          // SKU illustration is cosmetic. A missing counterpart in the modal
          // must not prevent the confirmed variant from becoming usable.
          try {
            if (typeof processVariants === 'function') processVariants();
          } catch (error) {
            console.warn('Product option illustrations could not be refreshed', error);
          }
          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      // Legacy cube/layer/crop widgets are different native applications. A
      // selected variant crossing the direct-metafield boundary starts with
      // fresh server-rendered controls, never the previous variant's proof.
      redirectForEditorRoute(html, variant, productUrl) {
        const incoming = html.querySelector('product-info[data-gio-editor-route]');
        const currentRoute = this.dataset.gioEditorRoute;
        const nextRoute = incoming?.dataset.gioEditorRoute;
        if (!variant?.id || !currentRoute || !nextRoute || currentRoute === nextRoute) return false;
        const destination = this.editorVariantUrl(productUrl, variant.id);
        this.pendingRequestUrl = destination.href;
        this.dataset.gioEditorRouteChanging = 'true';
        this.productForm?.toggleSubmitButton(true);
        document.querySelectorAll('#nextButton,.gio-editor-v2-primary,[data-cube-add],[data-cube-open],#customButton,#sunzi-button').forEach((button) => {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        });
        // Keep every cart path blocked until navigation destroys this page.
        document.addEventListener('submit', (event) => { event.preventDefault(); event.stopImmediatePropagation(); }, true);
        window.location.assign(destination.href);
        return true;
      }

      editorVariantUrl(productUrl, variantId) {
        const destination = new URL(productUrl, window.location.href);
        const current = new URL(window.location.href);
        current.searchParams.forEach((value, key) => {
          if (!['variant', 'section_id', 'option_values'].includes(key) && !destination.searchParams.has(key)) destination.searchParams.set(key, value);
        });
        destination.searchParams.delete('section_id');
        destination.searchParams.delete('option_values');
        if (variantId) destination.searchParams.set('variant', String(variantId));
        else destination.searchParams.delete('variant');
        destination.hash = current.hash;
        return destination;
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.updateOptionalComponent('share-button', 'updateUrl', [
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        ]);

        if (this.dataset.updateUrl === 'false') return;
        const destination = this.dataset.gioEditorRoute
          ? this.editorVariantUrl(url, variantId).href
          : `${url}${variantId ? `?variant=${variantId}` : ''}`;
        // Variant changes must retain the editor's same-document Back state.
        window.history.replaceState(window.history.state, '', destination);
        try { window.GIOEditorHistory?.rememberURL(destination); } catch (_) {}
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover') && typeof enableZoomOnHover === 'function') enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.updateOptionalComponent('media-gallery', 'setActiveMedia', [
          `${this.dataset.section}-${variantFeaturedMediaId}`, true
        ]);

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
